import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import {
  BiData,
  SetMultiImageDisplayModeClickBiEvent,
} from '@wix/da-bi/pkg/events';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import CarouselIcon from '@wix/da-ds/pkg/Icons/24x24/Carousel';
import { Dims } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import noop from '../../utils/noop';
import EmptyBackground from '../../DeviationBackground/StageBackground/EmptyBackground';
import {
  ADDITIONAL_MEDIA_ID_PREFIX,
  BI_SCROLL_SECTIONNAME,
  SCROLL_MODE_THRESHOLD_FOR_INVIEW,
} from '../constants';
import { useMobileStage } from '../hooks/useMobileStage';
import { useScrollToCurrentlyViewed } from '../hooks/useScrollToCurrentlyViewed';
import FullImageSourceWithCaption from '../_partials/FullImageSourceWithCaption';
import { DefaultArtStageProps as Props } from '../types';

import styles from './MobileArtStage.scss';

/** Multi-image deviation that displays as a vertical list of images one above the other */
const MobileMultiArtScrollStage: React.FC<Props> = ({
  deviation,
  additionalMedia = [],
  currentlyViewedImageSource,
  setCurrentlyViewedImageSource = noop,
  isZoomed,
  onEnterZoomMode = noop,
  onExitZoomMode = noop,
  setDisplayMode = noop,
}) => {
  const { t } = useTranslation();
  const { contentSize = {} as Dims } = useMobileStage(deviation);

  // if there's a currentlyViewedImageSource, scroll to it
  const isScrolling = useScrollToCurrentlyViewed(
    deviation,
    additionalMedia,
    currentlyViewedImageSource
  );

  return (
    <BiLoggerContextProvider value={BI_SCROLL_SECTIONNAME}>
      <EmptyBackground />

      <IconButton
        className={styles['switch-to-carousel-button']}
        icon={CarouselIcon}
        size="large"
        aria-label={t('deviationview.artstage.changeToCarouselMode.altLabel')}
        onClick={() => {
          setDisplayMode('carousel');
          window.scrollTo(0, 0);
        }}
        biData={BiData<SetMultiImageDisplayModeClickBiEvent>({
          evid: 309,
          deviationid: deviation.deviationId,
          view_mode: 'carousel',
        })}
      />

      <ol
        className={classnames(
          styles['scroll-mode-list'],
          isScrolling && styles['scroll-mode-list-scrolling']
        )}
      >
        <InView
          key="original-deviation"
          as="li"
          threshold={SCROLL_MODE_THRESHOLD_FOR_INVIEW}
          onChange={inView => {
            inView && !isScrolling && setCurrentlyViewedImageSource(deviation);
          }}
        >
          <FullImageSourceWithCaption
            key="original-deviation"
            id="deviation-image"
            deviationOrAttachment={deviation}
            dontExpandWrapper
            withoutZoomModal
            {...contentSize}
            isZoomed={isZoomed}
            onEnterZoomMode={onEnterZoomMode}
            onExitZoomMode={onExitZoomMode}
          />
        </InView>

        {additionalMedia.map((image, i) => {
          return (
            <InView
              key={`additional-media-${image.filename}`}
              as="li"
              threshold={SCROLL_MODE_THRESHOLD_FOR_INVIEW}
              onChange={inView => {
                inView && !isScrolling && setCurrentlyViewedImageSource(image);
              }}
            >
              <FullImageSourceWithCaption
                key={`additional-media-${image.filename}`}
                id={`${ADDITIONAL_MEDIA_ID_PREFIX}${i}`}
                deviationOrAttachment={image}
                dontExpandWrapper
                withoutZoomModal
                {...contentSize}
                isZoomed={isZoomed}
                onEnterZoomMode={onEnterZoomMode}
                onExitZoomMode={onExitZoomMode}
              />
            </InView>
          );
        })}
      </ol>
    </BiLoggerContextProvider>
  );
};
MobileMultiArtScrollStage.displayName = 'MobileMultiArtScrollStage';
export default MobileMultiArtScrollStage;
